import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { OrderStatus } from './types'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('es-CR', {
    style: 'currency',
    currency: 'CRC',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

/** Explicit alias — same as formatCurrency, use for clarity */
export const formatCurrencyCRC = formatCurrency

export function getStatusLabel(
  status: OrderStatus,
  restaurantId?: string,
): string {
  const isCapricho = String(restaurantId) === '2'
  const labels: Record<OrderStatus, string> = {
    new: 'Pedido recibido',
    accepted: 'Pedido aceptado',
    preparing: 'En preparación',
    ready: isCapricho ? 'Listo para entrega' : 'Listo',
    searching_driver: 'Buscando conductor',
    out_for_delivery: 'En camino',
    delivered: 'Entregado',
    cancelled: 'Cancelado',
  }
  return labels[status] || status
}

export function getStatusColor(status: OrderStatus): string {
  const colors: Record<OrderStatus, string> = {
    new: 'bg-blue-100 text-blue-800',
    accepted: 'bg-indigo-100 text-indigo-800',
    preparing: 'bg-amber-100 text-amber-800',
    ready: 'bg-emerald-100 text-emerald-800',
    searching_driver: 'bg-orange-100 text-orange-800',
    out_for_delivery: 'bg-purple-100 text-purple-800',
    delivered: 'bg-gray-100 text-gray-800',
    cancelled: 'bg-red-100 text-red-800',
  }
  return colors[status] || 'bg-gray-100 text-gray-800'
}

export function getOrderStatusSteps(restaurantId?: string): OrderStatus[] {
  const isCapricho = String(restaurantId) === '2'
  if (isCapricho) {
    // Capricho de Canela: no delivery, no driver search
    return ['new', 'accepted', 'preparing', 'ready', 'delivered']
  }
  return [
    'new',
    'accepted',
    'preparing',
    'ready',
    'searching_driver',
    'out_for_delivery',
    'delivered',
  ]
}

export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) return 'Hace un momento'

  const diffInMinutes = Math.floor(diffInSeconds / 60)
  if (diffInMinutes < 60) return `Hace ${diffInMinutes} min`

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) return `Hace ${diffInHours} h`

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays === 1) return 'Ayer'

  return date.toLocaleDateString('es-CR')
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param lat1 Latitude of point 1
 * @param lng1 Longitude of point 1
 * @param lat2 Latitude of point 2
 * @param lng2 Longitude of point 2
 * @returns Distance in kilometers
 */
export function calculateDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371 // Earth's radius in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLng = ((lng2 - lng1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

/**
 * Capricho de Canela delivery zone centers
 * Maximum 5km radius from any of these points
 */
export const CAPRICHO_DELIVERY_ZONES = [
  { name: 'San Marcos de Tarrazú', lat: 9.5667, lng: -84.0 },
  { name: 'Santa María de Dota', lat: 9.55, lng: -83.95 },
  { name: 'San Pablo de León Cortés', lat: 9.6548615, lng: -83.9997203 },
]

/**
 * Check if coordinates are within Capricho delivery zones (5km radius)
 * @returns { valid: boolean, nearestZone?: string, distance?: number }
 */
export function validateCaprichoDeliveryZone(
  lat: number,
  lng: number,
): { valid: boolean; nearestZone?: string; distance?: number } {
  let minDistance = Infinity
  let nearestZone = ''

  for (const zone of CAPRICHO_DELIVERY_ZONES) {
    const distance = calculateDistance(lat, lng, zone.lat, zone.lng)
    if (distance < minDistance) {
      minDistance = distance
      nearestZone = zone.name
    }
  }

  return {
    valid: minDistance <= 5,
    nearestZone,
    distance: minDistance,
  }
}
